/**
 * The public intake page's only route to the server (register item 35).
 *
 * Every call here is unauthenticated — the client has no account yet, and will not have one until
 * they have paid. Note what is NOT in this file: nothing sends a price. The page tells the server
 * which plan and which court, and the server answers with the amount. A page that posts an amount is
 * a page that can be edited to post $1.
 */

/**
 * Assigned rather than returned from an arrow function on purpose. Webpack substitutes the whole of
 * `process.env` as an object literal, and `() => {…}.API_HOST` parses that literal as a block, which
 * fails the build. In assignment position it is unambiguously an expression.
 */
const API_HOST: string = process.env.API_HOST || '';

const host = (): string => API_HOST;

export interface IntakeTierDto {
  tier: 'GOOD' | 'BETTER' | 'BEST';
  badge: string | null;
  copy: string[];
  fromPriceCents: number | null;
  fromPriceFormatted: string | null;
  /** True when the real figure depends on which court the case is in. */
  courtDependent: boolean;
}

/** A plan that is not sold online, and what the client is told instead (Lloyd, 14 Oct 2026). */
export interface IntakeByPhoneDto {
  tier: 'GOOD' | 'BETTER' | 'BEST';
  message: string;
}

export interface IntakeCourtDto {
  guid: string;
  name: string;
  county: string | null;
  city?: string | null;
}

export interface IntakeQuoteDto {
  sessionGuid: string;
  /** False when this case needs a person to price it. Not an error — a different path. */
  quotable: boolean;
  amountCents: number | null;
  amountFormatted: string | null;
  basis: 'JUVENILE_FLAT' | 'LIST_PRICE' | 'COURT_SURCHARGE' | 'REFUSED';
  explanation: string;
}

export interface IntakeFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  citationNumber: string;
  offenseDescription: string;
  city: string;
  county: string;
}

class PublicIntakeApi {
  static async getPricing(
    product: string,
  ): Promise<{ tiers: IntakeTierDto[]; byPhone?: IntakeByPhoneDto[] }> {
    const response = await fetch(
      `${host()}/api/v1/public/intake/pricing?product=${encodeURIComponent(product)}`,
    );
    if (!response.ok) throw new Error('This plan is not available online right now.');
    return response.json();
  }

  static async getCourts(): Promise<IntakeCourtDto[]> {
    const response = await fetch(`${host()}/api/v1/public/intake/courts`);
    if (!response.ok) throw new Error('Could not load the list of courts.');
    const data = await response.json();
    return data.courts;
  }

  /**
   * What this will cost, given the plan, the court and the client's age. Stores nothing.
   *
   * Called as soon as the court is chosen so the real figure — including a surcharge that can take
   * the Best plan from $600 to $4,900 — is on screen while the client can still change their mind.
   */
  static async getQuote(params: {
    product: string;
    tier: string;
    courtGuid: string | null;
    dateOfBirth: string | null;
  }): Promise<Omit<IntakeQuoteDto, 'sessionGuid'>> {
    const response = await fetch(`${host()}/api/v1/public/intake/quote`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!response.ok) throw new Error('We could not work out the fee for this court.');
    return response.json();
  }

  /** Holds the form and returns the real price. Creates nothing the client would ever see. */
  static async createSession(params: {
    product: string;
    tier: string;
    courtGuid: string;
    formData: IntakeFormData;
  }): Promise<IntakeQuoteDto> {
    const response = await fetch(`${host()}/api/v1/public/intake/session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!response.ok) {
      throw new Error('We could not save your details. Please check them and try again.');
    }
    return response.json();
  }

  /** Returns the Stripe URL to send the client to. */
  static async startCheckout(sessionGuid: string): Promise<string> {
    const response = await fetch(`${host()}/api/v1/public/intake/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionGuid }),
    });

    if (response.status === 409) {
      const data = await response.json();
      throw new Error(data.explanation || 'This case needs a quote from our office.');
    }
    if (response.status === 410) {
      throw new Error('This quote has expired. Please start again.');
    }
    if (!response.ok) throw new Error('We could not start the payment. Please try again.');

    const data = await response.json();
    return data.checkout_url;
  }
}

export default PublicIntakeApi;
